// libs
import React from 'react';

// components
import { Badge, Delete } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Checkbox, LinkButton, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import RichTextViewer from '@/components/RichTextViewer';
import { BadgeType } from '@/types/schema';

// styles

function BadgeCard({
  badge,
  isSelected,
  className, // flex-shrink-0: safari fix - ui collapsing in some safari versions
  onSelect,
  onDelete,
  onReplace,
  disabled,
}: {
  badge: BadgeType;
  isSelected?: boolean;
  className?: string;
  onSelect?: (badgeId: string, isSelected?: boolean) => void;
  onDelete?: () => void;
  onReplace?: () => void;
  disabled?: boolean;
}) {
  return (
    <Stack direction="col" gapY={12}>
      <Box
        className={cn(
          className ??
            cn(
              'mt-12 flex-shrink-0 rounded border py-20 ps-16 pe-24',
              isSelected ? 'border-focus-primary' : 'border-divider',
            ),
          'group',
          { 'cursor-pointer': !!onSelect },
        )}
        onClick={onSelect && !disabled ? () => onSelect(badge.id, isSelected) : undefined}>
        <Box className="flex flex-1 items-center justify-between">
          <Box className="flex align-center">
            {badge?.media?.url ? (
              <Image
                fill
                alt="badge"
                className="rounded-full !min-w-[5rem] mr-12 !w-[5rem] !h-[5rem]"
                src={badge.media?.url!}
              />
            ) : (
              <Badge
                className="h-[5rem] !min-w-[5rem] w-[5rem] mr-12"
                fillColorToken="yellow-600"
              />
            )}
            <Box>
              <Typography color="text-text-primary" variant="label-01">
                {badge.name}
              </Typography>
              <RichTextViewer
                className="mt-2"
                color="text-text-secondary"
                html={badge.description ?? ''}
                numberOfLines={2}
                variant="body-short-02"
              />
            </Box>
          </Box>
          <Box className="space-s-12">
            {onSelect ? (
              <Checkbox
                checked={isSelected}
                data-attr="SELECT"
                data-id={badge.id}
                variant="circle"
              />
            ) : null}

            {onDelete ? (
              <LinkButton
                className="py-2 px-4 opacity-0 transition ease-in group-hover:opacity-100"
                color={'internal'}
                disabled={disabled}
                onClick={onDelete}>
                <Delete fillColorToken="icon-placeholder" />
              </LinkButton>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}

export default React.memo(BadgeCard);
